// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useCoins from '@hooks/queries/cn/useCoin';
import useDsoParty from '@hooks/queries/cn/useDsoParty';
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import React from 'react';
import { ButtonProps } from '@mui/material';
import { CommercialAgreement } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { CommercialAgreementOffer } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Offer';
import { ContractId, DisclosedContract } from '@daml/types';
import { useParties } from '../../contexts/PartiesContext';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import { useAcceptCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useAcceptCommercialAgreementOffer';
import { useCancelCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useCancelCommercialAgreementOffer';
import { useCommercialAgreementLockCoin } from '../../hooks/mutations/commercialAgreements/useCommercialAgreementLockCoin';
import { useFlushExpiredCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useFlushExpiredCommercialAgreement';
import { useModifyCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useModifyCommercialAgreement';
import { useRejectCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useRejectCommercialAgreementOffer';
import { useRevokeCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useRevokeCommercialAgreement';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { getTransferPreapproval } from '../../utils/getTransferPreapproval';

interface Payload {
  operator: string;
  user: string;
  feeReceiver: string;
  utilityFees: string;
  ratePerDay?: string;
  billingPeriodMinutes?: string;
  rewardReceiver?: string;
}

export type Row = {
  contractId: string;
  state: string;
  payload: Payload;
  reason?: string;
};
type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
  buttonProps?: ButtonProps;
};

export const useCreateCommercialAgreementActions = () => {
  const { party, operator } = useParties();
  const isOperator = operator === party;
  const dsoParty = useDsoParty()?.data || '';
  const amulets = useCoins();
  const amuletCids = (amulets.data ?? []).map((a) => a.contractId);
  const revokeCommercialAgreement = useRevokeCommercialAgreement();
  const flushExpiredCommercialAgreement = useFlushExpiredCommercialAgreement();
  const modifyCommercialAgreement = useModifyCommercialAgreement();
  const commercialAgreementLockCoin = useCommercialAgreementLockCoin();
  const rejectCommercialAgreementOffer = useRejectCommercialAgreementOffer();
  const acceptCommercialAgreementOffer = useAcceptCommercialAgreementOffer();
  const cancelCommercialAgreementOffer = useCancelCommercialAgreementOffer();
  const [onReject, setOnReject] = React.useState<
    ((reason: string) => Promise<unknown>) | undefined
  >();
  const scanProxyApi = useScanProxyApiClient();

  const [onModify, setOnModify] = React.useState<
    | ((
        feeReceiver: string,
        utilityFees: string,
        billingPeriodMinutes?: string,
        rate?: string,
        rewardReceiver?: string,
      ) => Promise<unknown>)
    | undefined
  >();
  const [onChangeTopupAmount, setOnChangeTopupAmount] = React.useState<
    ((amount: string) => Promise<unknown>) | undefined
  >();
  const [onRevoke, setOnRevoke] = React.useState<(() => Promise<unknown>) | undefined>();
  const [currFeeReceiver, setCurrFeeReceiver] = React.useState('');
  const [currRewardReceiver, setCurrRewardReceiver] = React.useState('');
  const [currUtilityFees, setCurrUtilityFees] = React.useState('');
  const [currRate, setCurrRate] = React.useState<string | undefined>();
  const [currBillingPeriodMinutes, setCurrBillingPeriodMinutes] = React.useState<
    string | undefined
  >();
  const rejectCommercialAgreementOff = React.useCallback(
    async (row: Row, reason: string) => {
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreementOffer>,
        argument: { reason },
      };

      await rejectCommercialAgreementOffer(payload);
    },
    [rejectCommercialAgreementOffer],
  );
  const modifyCommercialAgr = React.useCallback(
    async (
      row: Row,
      feeReceiver: string,
      utilityFees: string,
      billingPeriodMinutes?: string,
      rate?: string,
      rewardReceiver?: string,
    ) => {
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreement>,
        argument: {
          feeReceiver,
          utilityFees: {
            credentialBillingFeeUsd: utilityFees,
            baseFee:
              !billingPeriodMinutes || !rate
                ? null
                : {
                    billingPeriodMinutes,
                    feePerDayUsd: {
                      rate,
                    },
                  },
          },
          rewardReceiver: !rewardReceiver ? null : rewardReceiver,
        },
      };
      await modifyCommercialAgreement(payload);
    },
    [modifyCommercialAgreement],
  );

  const acceptCommercialAgreementOff = React.useCallback(
    async (row: Row) => {
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreementOffer>,
        argument: {
          user: row.payload.user,
          utilityFees: { credentialBillingFeeUsd: row.payload.utilityFees, baseFee: null },
          operator: row.payload.operator,
          feeReceiver: row.payload.operator,
          dso: dsoParty,
        },
      };
      await acceptCommercialAgreementOffer(payload);
    },
    [acceptCommercialAgreementOffer, dsoParty],
  );
  const cancelCommercialAgreementOff = React.useCallback(
    async (row: Row) => {
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreementOffer>,
        argument: {},
      };
      await cancelCommercialAgreementOffer(payload);
    },
    [cancelCommercialAgreementOffer],
  );
  const enableAccruedFees = useIsPackageAvailableFrom(
    'utility-commercials-v0',
    [party, operator],
    '0.3.0',
  );
  const revokeCommercialAgreementOff = React.useCallback(
    async (row: Row) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );
      let transferPreapprovalCid = null;
      let disclosedPreapproval: DisclosedContract[] = [];

      if (enableAccruedFees.data) {
        const result = await getTransferPreapproval(scanProxyApi, row.payload.feeReceiver);
        transferPreapprovalCid = result.transferPreapproval.contractId;
        disclosedPreapproval = result.disclosedPreapproval;
      }
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreement>,
        feeReceiver: row.payload.feeReceiver,
        argument: {
          transferContext: appTransferContext,
          actor: party,
          transferPreapprovalCid,
        },
        meta: { disclosedContracts: [...disclosedContracts, ...disclosedPreapproval] },
      };
      await revokeCommercialAgreement(payload);
    },
    [revokeCommercialAgreement, party, scanProxyApi, enableAccruedFees.data],
  );
  const commercialAgreementLockC = React.useCallback(
    async (row: Row, amount: string) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );
      const payload = {
        argument: {
          targetAmount: amount,
          coinCids: amuletCids,
          transferContext: appTransferContext,
        },
        meta: {
          disclosedContracts,
        },
        cid: row.contractId as ContractId<CommercialAgreement>,
      };
      await commercialAgreementLockCoin(payload);
    },
    [scanProxyApi, commercialAgreementLockCoin, amuletCids],
  );

  const flushExpiredCommercialAgreementDeposit = React.useCallback(
    async (row: Row) => {
      const payload = {
        cid: row.contractId as ContractId<CommercialAgreement>,
        argument: { actor: party },
      };

      await flushExpiredCommercialAgreement(payload);
    },
    [flushExpiredCommercialAgreement, party],
  );

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.state === 'Offered' && r.payload.user === party)
      actions.push(
        { name: 'Accept', action: () => Promise.resolve(acceptCommercialAgreementOff(r)) },
        {
          name: 'Reject',
          buttonProps: {
            variant: 'outlined',
            color: 'error',
          },
          action: () => {
            setOnReject(() => (reason: string) => rejectCommercialAgreementOff(r, reason));
            return Promise.resolve();
          },
        },
      );
    if (r.state === 'Offered' && isOperator) {
      actions.push({
        name: 'Cancel',
        action: cancelCommercialAgreementOff,
      });
    }
    if (r.state === 'Accepted') {
      if (r.payload.user === party) {
        actions.push({
          name: 'Top up',
          action: () => {
            setOnChangeTopupAmount(() => (amount: string) => commercialAgreementLockC(r, amount));
            return Promise.resolve();
          },
        });
      }

      if (isOperator) {
        actions.push({
          name: 'Modify',
          action: () => {
            setCurrFeeReceiver(r.payload.feeReceiver);
            setCurrRewardReceiver(r.payload?.rewardReceiver ?? '');
            setCurrUtilityFees(r.payload.utilityFees);
            setCurrRate(r.payload?.ratePerDay);
            setCurrBillingPeriodMinutes(r.payload?.billingPeriodMinutes);
            setOnModify(
              () =>
                (
                  feeReceiver: string,
                  utilityFees: string,
                  billingPeriodMinutes?: string,
                  rate?: string,
                  rewardReceiver?: string,
                ) =>
                  modifyCommercialAgr(
                    r,
                    feeReceiver,
                    utilityFees,
                    billingPeriodMinutes,
                    rate,
                    rewardReceiver,
                  ),
            );
            return Promise.resolve();
          },
        });

        actions.push({
          name: 'Flush',
          buttonProps: {
            variant: 'outlined',
            color: 'warning',
          },
          action: () => Promise.resolve(flushExpiredCommercialAgreementDeposit(r)),
        });
      }
      actions.push({
        name: 'Revoke',
        buttonProps: {
          variant: 'outlined',
        },
        action: () => {
          setOnRevoke(() => () => revokeCommercialAgreementOff(r));
          return Promise.resolve();
        },
      });
    }
    return actions;
  };
  return {
    createActions,
    onReject,
    setOnReject,
    onModify,
    setOnModify,
    currFeeReceiver,
    currUtilityFees,
    currRate,
    currRewardReceiver,
    currBillingPeriodMinutes,
    onChangeTopupAmount,
    setOnChangeTopupAmount,
    onRevoke,
    setOnRevoke,
  };
};
